import React, { FC, useContext, useMemo } from 'react'
import { slugs } from '@/constants'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { tlrPromos } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketBannerSection'
import EarlyBirdModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/EarlyBirdModal'
import FlashSaleModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/FlashSaleModal'
import { GiveawayModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GiveawayModal'
import { GuildGiveawayModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GuildGiveawayModal'
import { LastRodeoEventModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/LastRodeoEventModal'
import { NoFeesModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/NoFeesModal'
import { TexasModalContext } from '@/views/TicketCheckoutViews/ShowtimesView/contexts/TexasModalContext'
import { useTheatricalModalController } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useTheatricalModalController'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface Props {
  projectName: TheatricalReleaseObject['title']
  theatricalSlug: string
  isFortWorth: boolean
}

export const TicketPromoModals: FC<Props> = ({ theatricalSlug, projectName }) => {
  const { promo, isReady } = useTheatricalPromotionContext()
  const [isModalOpen, closeModal] = useTheatricalModalController(promo, theatricalSlug, isReady)
  const texasModalContext = useContext(TexasModalContext)

  const { promoType, discount, code } = promo ?? {}
  const { areGuildTicketsAvailable, loading: guildTicketsLoading } = useGuildTickets(theatricalSlug)
  const { query } = useSafeRouter()
  const isGiftFlow = query?.flow === 'guild-gift' || query?.flow === 'gift-tickets'

  const queryTlrPromo = useMemo(() => {
    const queryPromo = getItemOrFirstEntry(query?.promo)
    if (!queryPromo || theatricalSlug !== slugs.theLastRodeo) return null

    return tlrPromos[queryPromo.toLowerCase()]
  }, [query?.promo, theatricalSlug])

  if (theatricalSlug === slugs.theLastRodeo && !isGiftFlow) {
    return (
      <LastRodeoEventModal
        isOpen={texasModalContext.isModalOpen}
        onClose={() => {
          texasModalContext.closeModal()
        }}
        theatricalSlug={theatricalSlug}
      />
    )
  }

  if (!isReady || !promoType || isGiftFlow || queryTlrPromo) return null

  if (promoType !== 'GIVEAWAY') {
    return (
      <>
        {promoType === 'FLASH_SALE' && (
          <FlashSaleModal theatricalName={projectName} open={isModalOpen} onClose={closeModal} discount={discount} />
        )}

        {promoType === 'EARLY_BIRD' && (
          <EarlyBirdModal
            theatricalSlug={theatricalSlug}
            onClose={closeModal}
            open={isModalOpen}
            code={code}
            discount={discount}
          />
        )}

        {promoType === 'NO_FEES' && !!code && (
          <NoFeesModal
            code={code}
            isManualCode
            open={isModalOpen}
            onClose={closeModal}
            theatricalSlug={theatricalSlug}
          />
        )}
      </>
    )
  }

  if (guildTicketsLoading) return null
  if (areGuildTicketsAvailable) {
    return <GuildGiveawayModal isOpen={isModalOpen} onClose={closeModal} theatricalSlug={theatricalSlug} />
  } else {
    return <GiveawayModal isOpen={isModalOpen} onClose={closeModal} theatricalSlug={theatricalSlug} />
  }
}

export default TicketPromoModals
